import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Header, Icon, Loader, Modal, Segment, Button, List } from 'semantic-ui-react';
import type {
  FastchipClusterStockBalanceResponse,
  FastchipClusterStockSegmentBalance,
  FastchipStockSectionAlert,
} from '../../../types/dtos';
import { DesignSystem } from '../../../styles/designSystem';
import { isEr2ClusterEnabled } from '../../../utils/fastchipClusterRules';

const ALERT_DISMISS_PREFIX = 'gettel-fastchip-stock-alert-dismissed:';

type ContractsFastchipStockSummaryProps = {
  balance: FastchipClusterStockBalanceResponse | null;
  loading?: boolean;
};

const REGION_ACCENTS: Record<string, string> = {
  SC: '#2185d0',
  SP: '#21ba45',
  RJMG: '#f2711c',
  DH: '#db2828',
};

const PR_ACCENTS: Record<string, string> = {
  PR_ER_PHYSICAL: '#6435c9',
  PR_ER_ESIM: '#6f42c1',
  PR_MH_PHYSICAL: '#008080',
  PR_MH_ESIM: '#00b5ad',
};

const SENIOR_ACCENTS: Record<string, string> = {
  HB: '#e03997',
  CP: '#2185d0',
  TPCL: '#a5673f',
  BA: '#f2711c',
  HDM2: '#6435c9',
};

function isAlertDismissed(sectionKey: string): boolean {
  try {
    return sessionStorage.getItem(`${ALERT_DISMISS_PREFIX}${sectionKey}`) === '1';
  } catch {
    return false;
  }
}

function dismissAlert(sectionKey: string): void {
  try {
    sessionStorage.setItem(`${ALERT_DISMISS_PREFIX}${sectionKey}`, '1');
  } catch {
    /* ignore */
  }
}

function MiniStockCard({
  segment,
  accent,
  icon = 'microchip',
}: {
  segment: FastchipClusterStockSegmentBalance;
  accent: string;
  icon?: string;
}) {
  const isCritical = segment.disponivel === 0;
  const isLow = segment.disponivel > 0 && segment.disponivel <= 10;

  return (
    <Segment
      style={{
        borderRadius: DesignSystem.borderRadius.medium,
        boxShadow: DesignSystem.shadows.subtle,
        border: `1px solid ${DesignSystem.colors.neutral.gray.medium}`,
        borderLeft: `4px solid ${accent}`,
        background: DesignSystem.colors.neutral.white,
        minHeight: 96,
        padding: '0.85rem 0.9rem',
        margin: 0,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name={icon as 'microchip'} style={{ color: accent, margin: 0 }} />
            <span
              style={{
                fontWeight: 600,
                fontSize: '12px',
                color: DesignSystem.colors.neutral.gray.darker,
                lineHeight: 1.2,
              }}
            >
              {segment.label}
            </span>
          </div>
          {(isCritical || isLow) && (
            <span
              style={{
                display: 'inline-block',
                marginTop: 6,
                fontSize: '10px',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: isCritical ? '#c0392b' : '#d68910',
              }}
            >
              {isCritical ? 'Esgotado' : 'Finalizando'}
            </span>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: accent, lineHeight: 1 }}>{segment.disponivel}</div>
          <div style={{ fontSize: '10px', color: DesignSystem.colors.neutral.gray.dark }}>disp.</div>
        </div>
      </div>
      <div
        style={{
          marginTop: 8,
          fontSize: '11px',
          color: DesignSystem.colors.neutral.gray.dark,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>{segment.total} total</span>
        <span>{segment.percentualDisponivel.toFixed(1)}%</span>
      </div>
    </Segment>
  );
}

const ContractsFastchipStockSummary: React.FC<ContractsFastchipStockSummaryProps> = ({ balance, loading }) => {
  const [pendingAlerts, setPendingAlerts] = useState<FastchipStockSectionAlert[]>([]);
  const [alertModalOpen, setAlertModalOpen] = useState(false);

  const regionSegments = useMemo(() => {
    if (!balance) return [];
    // E.R.2 só quando `ER2_CLUSTER_ENABLED`; DH = stock tagueado (mercado activo RJ/MG/ES).
    const er2 =
      isEr2ClusterEnabled() && balance.rioMinas
        ? {
            ...balance.rioMinas,
            label:
              balance.rioMinas.label?.includes('E.R.2') || balance.rioMinas.label?.includes('ER2')
                ? balance.rioMinas.label
                : 'E.R.2 — SP',
          }
        : null;
    const dh =
      balance.dh ??
      ({
        key: 'DH',
        label: 'DH — RJ / MG / ES',
        disponivel: 0,
        total: 0,
        percentualDisponivel: 0,
      } satisfies FastchipClusterStockSegmentBalance);
    return [balance.santaCatarina, balance.saoPaulo, er2, dh].filter(
      (s): s is FastchipClusterStockSegmentBalance => !!s
    );
  }, [balance]);

  const paranaSegments = useMemo(() => {
    if (!balance) return [];
    return [balance.paranaErPhysical, balance.paranaErEsim, balance.paranaMhPhysical, balance.paranaMhEsim];
  }, [balance]);

  const seniorSegments = useMemo(() => {
    if (!balance) return [];
    return [balance.hb, balance.cp, balance.tpcl, balance.ba, balance.hdm2];
  }, [balance]);

  useEffect(() => {
    if (!balance?.alerts?.length) {
      setPendingAlerts([]);
      setAlertModalOpen(false);
      return;
    }

    const unseen = balance.alerts.filter((alert) => !isAlertDismissed(alert.sectionKey));
    setPendingAlerts(unseen);
    if (unseen.length > 0) {
      setAlertModalOpen(true);
    }
  }, [balance]);

  const handleDismissAlerts = () => {
    pendingAlerts.forEach((alert) => dismissAlert(alert.sectionKey));
    setAlertModalOpen(false);
    setPendingAlerts([]);
  };

  if (loading && !balance) {
    return (
      <Segment placeholder style={{ marginTop: '1rem', marginBottom: '1rem', minHeight: 120 }}>
        <Loader active inline="centered" size="small">
          Carregando estoque Fastchip...
        </Loader>
      </Segment>
    );
  }

  if (!balance) return null;

  return (
    <>
      <Segment
        style={{
          marginTop: '1rem',
          marginBottom: '1rem',
          borderRadius: DesignSystem.borderRadius.large,
          boxShadow: DesignSystem.shadows.card,
          border: `1px solid ${DesignSystem.colors.neutral.gray.medium}`,
          background: DesignSystem.colors.section.gradient,
          padding: '1rem 1.1rem 1.15rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
          <Header as="h4" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="chart pie" color="blue" />
            Estoque Fastchip disponível
          </Header>
          {loading && <Loader active inline size="mini" />}
        </div>
        <p style={{ margin: '0 0 12px', fontSize: '12px', color: DesignSystem.colors.neutral.gray.dark }}>
          ICCIDs disponíveis por cluster — apenas consulta. Atualiza ao entrar na página ou ao clicar em Atualizar.
        </p>

        <Header as="h5" style={{ margin: '0 0 8px', fontSize: '12px', color: DesignSystem.colors.neutral.gray.darker }}>
          {isEr2ClusterEnabled() ? 'Regiões (SC, SH legado, E.R.2, DH)' : 'Regiões (SC, SH legado, DH)'}
        </Header>
        <Grid stackable columns={isEr2ClusterEnabled() ? 4 : 3} style={{ marginBottom: 12 }}>
          {regionSegments.map((segment) => (
            <Grid.Column key={segment.key}>
              <MiniStockCard
                segment={segment}
                accent={REGION_ACCENTS[segment.key] ?? '#2185d0'}
                icon={
                  segment.key === 'SC'
                    ? 'map marker alternate'
                    : segment.key === 'SP'
                      ? 'building outline'
                      : segment.key === 'DH'
                        ? 'shipping fast'
                        : 'exchange'
                }
              />
            </Grid.Column>
          ))}
        </Grid>

        <Header as="h5" style={{ margin: '0 0 8px', fontSize: '12px', color: DesignSystem.colors.neutral.gray.darker }}>
          Paraná (FAST)
        </Header>
        <Grid stackable columns={4} style={{ marginBottom: 12 }}>
          {paranaSegments.map((segment) => (
            <Grid.Column key={segment.key}>
              <MiniStockCard segment={segment} accent={PR_ACCENTS[segment.key] ?? '#6435c9'} />
            </Grid.Column>
          ))}
        </Grid>

        <Header as="h5" style={{ margin: '0 0 8px', fontSize: '12px', color: DesignSystem.colors.neutral.gray.darker }}>
          Clusters Senior FAST
        </Header>
        <Grid stackable columns={4}>
          {seniorSegments.map((segment) => (
            <Grid.Column key={segment.key}>
              <MiniStockCard segment={segment} accent={SENIOR_ACCENTS[segment.key] ?? '#2185d0'} icon="globe" />
            </Grid.Column>
          ))}
        </Grid>
      </Segment>

      <Modal open={alertModalOpen} size="small" onClose={handleDismissAlerts}>
        <Modal.Header>
          <Icon name="warning sign" color="orange" />
          Atenção — estoque Fastchip
        </Modal.Header>
        <Modal.Content>
          <p style={{ marginBottom: 12, color: DesignSystem.colors.neutral.gray.darker }}>
            Alguns clusters estão com estoque baixo ou esgotado. Este aviso aparece uma vez por seção nesta sessão.
          </p>
          <List divided relaxed>
            {pendingAlerts.map((alert) => (
              <List.Item key={alert.sectionKey}>
                <List.Icon
                  name={alert.level === 'critical' ? 'times circle' : 'exclamation triangle'}
                  color={alert.level === 'critical' ? 'red' : 'orange'}
                />
                <List.Content>
                  <List.Header>{alert.title}</List.Header>
                  <List.Description>{alert.message}</List.Description>
                </List.Content>
              </List.Item>
            ))}
          </List>
        </Modal.Content>
        <Modal.Actions>
          <Button primary onClick={handleDismissAlerts}>
            Entendi
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default ContractsFastchipStockSummary;
